import React, { useEffect } from 'react';

import { BrowserRouter as Router, Routes, Route, Navigate }
    from "react-router-dom";
import { Outlet } from "react-router-dom";
import { AppScope } from './models/AppScope';
import { UtilService } from './services/UtilService';
import { MainLayout } from './components/MainLayout/MainLayout';
import Login from './pages/Login/Login';
import Home from './pages/PaySlip/PaySlip';
import ValidateIdentity from './pages/ValidateIdentity/ValidateIdentity';
import Register from './pages/Register/Register';
import ForgetPassword from './pages/ForgetPassword/ForgetPassword';
import ResetPassword from './pages/ResetPassword/ResetPassword';
import AdminUsers from './pages/AdminUsers/AdminUsers';
import PaySlip from './pages/PaySlip/PaySlip';
import HomeAdmin from './pages/ManagePensioners/ManagePensioners';
import ManagePensioners from './pages/ManagePensioners/ManagePensioners';
import Messages from './pages/Messages/Messages';
import Security from './pages/Security/Security';
import ChangeEmail from './pages/ChangeEmail/ChangeEmail';
import { useAuth } from './services/AuthContext';

import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import NotFound from './pages/NotFound/NotFound';
import VerifyEmail from './pages/VerifyEmail/VerifyEmail';
import LoginActivitiesView from './pages/LoginActivities/LoginActivities';
import { MessageInfo } from './models/MessageInfo';
import PayslipChangeReport from './pages/PayslipChangeReport/PayslipChangeReport';
import PaperPayslip from './pages/PaperPayslip/PaperPayslip';
import { Box, Button, Dialog, DialogActions, DialogContent, useMediaQuery, useTheme } from '@mui/material';
import AppLoadingSpinner from './components/Common/AppLoadingSpinner/AppLoadingSpinner';
import Footer from './components/Footer/AuthFooter';

const App: React.FC = () => {
  const [scope, apply] = React.useState<AppScope>({showMessageInfo: false});
  const loginContext = useAuth();
  const token = loginContext ? loginContext.token : '';
  const isAuthenticated = token === undefined || token === '' || token === null ? false : true;
  const [showMessageInfo, isMessageInfoShow] = React.useState<boolean>(false);
  const [messageInfo, setMessageInfo] = React.useState<MessageInfo>({
      message: '',
      messageType: 'danger',
      header: '',
      primaryText: 'OK',
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [appReady, setAppReady] = React.useState(false);

  useEffect(() => {
    const initializeApp = async () => {
      if (isAuthenticated && token && !loginContext.userDetailsLoaded) {
        await loginContext.getUserDetails(token);
      }
      setAppReady(true);
    };

    initializeApp();
  }, [loginContext?.token, loginContext?.userDetailsLoaded]);
  
  useEffect(() =>{
    applyScope();
  },[]);

  function applyScope(){
    apply(UtilService.cloneObject(scope));
  }

  const handleAction = (e: React.MouseEvent, callback?: (e: CustomEvent) => void) => {
    isMessageInfoShow(false); // Close dialog first
    callback?.(new CustomEvent('button-click', { detail: e }));
  };

  if (!appReady || (isAuthenticated && loginContext?.token && !loginContext.userDetailsLoaded)) {
    return <AppLoadingSpinner />
  }
  
  const AuthLayout = ({ isAuthenticated }: any) => {
    if (isAuthenticated) {
      return <Navigate to="/home" replace />;
    }
  
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: '100vh',
        overflow: 'hidden'
      }}>
        <Box sx={{
          flex: 1, // Takes all available space
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto' // Enable scrolling only for content
        }}>
          <Outlet />
        </Box>
        <Footer />
      </Box>
    );
    // return <Outlet />
  };

  return (
    <>
      <Router>
        <div className='root'>
          <Routes>
            <Route element={<AuthLayout isAuthenticated={isAuthenticated} />}>
              <Route path='/login' element={<Login setMessageInfo={setMessageInfo} isMessageInfoShow={isMessageInfoShow} />}/>
              <Route path='/validate' element={<ValidateIdentity setMessageInfo={setMessageInfo} isMessageInfoShow={isMessageInfoShow} />} />
              <Route path='/register' element={<Register setMessageInfo={setMessageInfo} isMessageInfoShow={isMessageInfoShow} />} />
              <Route path='/forget-password' element={<ForgetPassword setMessageInfo={setMessageInfo} isMessageInfoShow={isMessageInfoShow} />} />
              <Route path='/reset-password' element={<ResetPassword setMessageInfo={setMessageInfo} isMessageInfoShow={isMessageInfoShow} />} />
              <Route path='/verify-email' element={<VerifyEmail setMessageInfo={setMessageInfo} isMessageInfoShow={isMessageInfoShow} />} />
            </Route>

            {/* Following Pages will use Main Layout*/}
            { isAuthenticated ? (
              <Route path='/' element={<MainLayout />}>
                
                { loginContext.isAdmin === 'true' ? (
                  <>
                      <Route index element={<HomeAdmin />} />
                      <Route path='/home' element={<HomeAdmin />}/>
                      <Route path='/pensioners' element={<ManagePensioners />} />
                      { loginContext.isSuperAdmin === 'true' && (
                        <Route path='/admin-users' element={<AdminUsers />} />
                      )}
                      <Route path='/login-activities' element={<LoginActivitiesView />} />
                      <Route path='/payslip-change' element={<PayslipChangeReport />} />
                    </>
                ) : (
                  <>
                      <Route index element={<Home />} />
                      <Route path='/home' element={<Home />}/>
                      <Route path='/payslip' element={<PaySlip />} />
                      <Route path='/messages' element={<Messages />} />
                      <Route path='/security' element={<Security />} />
                      <Route path='/change-email' element={<ChangeEmail />} />
                      <Route path='/paper-payslip' element={<PaperPayslip />} />
                    </>
                )}
              </Route>
            ) : (
              <Route path='*' element={<Navigate to='/login' replace/>} />
            )}

            {/* Catch all unmatched routes */}
            <Route path='*' element={<NotFound />} />
          </Routes>
        </div>
      </Router>

    <Dialog
        open={showMessageInfo}
        onClose={() => isMessageInfoShow(false)}
        maxWidth="sm" // 'xs' | 'sm' | 'md' | 'lg' | 'xl'
        fullWidth={isMobile} // Full width on mobile
        sx={{
          '& .MuiDialog-paper': {
            minWidth: isMobile ? '90vw' : 400, // Responsive min-width
            p: 2,
          }
        }}
      >
        {/* Message Content */}
        <DialogContent>
          <Box
            sx={{
              whiteSpace: 'pre-line',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              maxHeight: '60vh',
              overflowY: 'auto',
              fontSize: isMobile ? '0.875rem' : '1rem',
            }}
          >
            {messageInfo.message}
          </Box>
        </DialogContent>

        {/* Action Buttons */}
        <DialogActions
          sx={{
            flexDirection: isMobile ? 'column' : 'row', // Stack vertically on mobile
            gap: 1,
            px: 2,
            pb: 2,
          }}
        >
          {messageInfo.cancelText && (
            <Button
              variant="outlined"
              onClick={(e) => handleAction(e, messageInfo.primaryCallBack)}
              fullWidth={isMobile} // Full width on mobile
            >
              {messageInfo.cancelText}
            </Button>
          )}

          {messageInfo.primaryText && (
            <Button
              variant="contained"
              onClick={(e) => handleAction(e, messageInfo.primaryCallBack)}
              fullWidth={isMobile} // Full width on mobile
              sx={{ ml: isMobile ? 0 : 1 }} // Add left margin only on desktop
            >
              {messageInfo.primaryText}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

export default App;
